'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Package, ChevronRight, AlertTriangle, ShieldCheck, Wrench } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

const products = [
  {
    id: 'prod-001',
    name: 'Samsung 65" QLED TV',
    model: 'QA65Q80CAKLXL',
    serial: 'SN-2409-SMTV-0041',
    category: 'Electronics',
    purchaseDate: '14 Sep 2023',
    warrantyExpiry: '13 Sep 2026',
    daysLeft: 473,
    status: 'active' as const,
    hasOpenService: true,
    vendor: 'Samsung India',
    invoiceRef: 'INV-2023-0914',
  },
  {
    id: 'prod-002',
    name: 'LG 8kg Front Load Washer',
    model: 'FHM1408BDL',
    serial: 'LG-WM-2312-00892',
    category: 'Home Appliances',
    purchaseDate: '22 Dec 2022',
    warrantyExpiry: '15 Jun 2026',
    daysLeft: 18,
    status: 'expiring' as const,
    hasOpenService: false,
    vendor: 'LG Electronics',
    invoiceRef: 'INV-2022-1222',
  },
  {
    id: 'prod-003',
    name: 'Dyson V15 Detect',
    model: 'SV22',
    serial: 'DY-V15-2604-11203',
    category: 'Home Appliances',
    purchaseDate: '05 Apr 2026',
    warrantyExpiry: '04 Apr 2028',
    daysLeft: 676,
    status: 'active' as const,
    hasOpenService: false,
    vendor: 'Dyson India',
    invoiceRef: 'INV-2026-0405',
  },
  {
    id: 'prod-004',
    name: 'Sony WH-1000XM5',
    model: 'WH1000XM5/B',
    serial: 'SN-WH5-2501-55671',
    category: 'Audio',
    purchaseDate: '10 Jan 2026',
    warrantyExpiry: '09 Jan 2027',
    daysLeft: 226,
    status: 'active' as const,
    hasOpenService: false,
    vendor: 'Sony India',
    invoiceRef: 'INV-2026-0110',
  },
  {
    id: 'prod-005',
    name: 'Voltas 1.5T Split AC',
    model: '185V CAZS',
    serial: 'VL-AC-2209-03341',
    category: 'Home Appliances',
    purchaseDate: '12 Sep 2022',
    warrantyExpiry: '11 Sep 2023',
    daysLeft: 0,
    status: 'expired' as const,
    hasOpenService: false,
    vendor: 'Voltas Ltd',
    invoiceRef: 'INV-2022-0912',
  },
  {
    id: 'prod-006',
    name: 'Bosch 12-Place Dishwasher',
    model: 'SMS66GI01I',
    serial: 'BS-DW-2310-07782',
    category: 'Kitchen Appliances',
    purchaseDate: '18 Oct 2023',
    warrantyExpiry: '17 Oct 2025',
    daysLeft: 0,
    status: 'expired' as const,
    hasOpenService: false,
    vendor: 'Bosch Home',
    invoiceRef: 'INV-2023-1018',
  },
  {
    id: 'prod-007',
    name: 'Apple MacBook Pro 14"',
    model: 'MNW83HN/A',
    serial: 'AP-MBP-2305-C4X21',
    category: 'Computers',
    purchaseDate: '20 May 2023',
    warrantyExpiry: '19 May 2024',
    daysLeft: 0,
    status: 'expired' as const,
    hasOpenService: false,
    vendor: 'Apple India',
    invoiceRef: 'INV-2023-0520',
  },
  {
    id: 'prod-008',
    name: 'Godrej 564L French Door Fridge',
    model: 'RF GF 5643PTJ',
    serial: 'GD-RF-2401-09921',
    category: 'Kitchen Appliances',
    purchaseDate: '03 Jan 2024',
    warrantyExpiry: '02 Jan 2029',
    daysLeft: 952,
    status: 'active' as const,
    hasOpenService: true,
    vendor: 'Godrej Appliances',
    invoiceRef: 'INV-2024-0103',
  },
];

export default function ProductOwnershipGrid() {
  const [filter, setFilter] = useState<'all' | 'active' | 'expiring' | 'expired'>('all');

  const filtered = products.filter((p) => {
    if (filter === 'all') return true;
    return p.status === filter;
  });

  const tabs = [
    { key: 'all', label: 'All Products', count: products.length },
    { key: 'active', label: 'Active', count: products.filter(p => p.status === 'active').length },
    { key: 'expiring', label: 'Expiring Soon', count: products.filter(p => p.status === 'expiring').length },
    { key: 'expired', label: 'Expired', count: products.filter(p => p.status === 'expired').length },
  ] as const;

  return (
    <div className="bg-card rounded-xl border border-border shadow-card">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-border">
        <div>
          <h3 className="text-sm font-600 text-foreground">Product Ownership</h3>
          <p className="text-xs text-muted-foreground">{products.length} products registered</p>
        </div>
        <Link href="/product-ownership-dashboard" className="text-xs text-primary font-500 hover:underline flex items-center gap-1">
          Manage all <ChevronRight size={12} />
        </Link>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 px-5 py-3 border-b border-border overflow-x-auto scrollbar-thin">
        {tabs.map((tab) => (
          <button
            key={`ptab-${tab.key}`}
            onClick={() => setFilter(tab.key)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-500 transition-all duration-150 ${filter === tab.key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'}`}
          >
            {tab.label}
            <span className={`text-[10px] rounded-full px-1.5 py-0.5 font-600 ${filter === tab.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4 p-5">
        {filtered.map((product) => (
          <div
            key={product.id}
            className={`relative rounded-xl border p-4 hover:shadow-card-hover transition-all duration-200 group cursor-pointer ${product.status === 'expiring' ? 'border-warning/40 bg-warning/3' : product.status === 'expired' ? 'border-border bg-muted/30' : 'border-border bg-background'}`}
          >
            {/* Top row */}
            <div className="flex items-start justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-primary/8 flex items-center justify-center flex-shrink-0">
                <Package size={18} className="text-primary" />
              </div>
              <div className="flex items-center gap-1.5">
                {product.hasOpenService && (
                  <span title="Open service request">
                    <Wrench size={13} className="text-accent" />
                  </span>
                )}
                <StatusBadge
                  variant={product.status}
                  label={product.status === 'active' ? 'Active' : product.status === 'expiring' ? 'Expiring' : 'Expired'}
                  size="sm"
                />
              </div>
            </div>

            {/* Product name */}
            <p className="text-sm font-600 text-foreground leading-snug mb-0.5 truncate">{product.name}</p>
            <p className="text-xs text-muted-foreground mb-3 truncate">{product.vendor}</p>

            {/* Serial */}
            <p className="text-[10px] font-mono-data text-muted-foreground bg-muted rounded px-1.5 py-0.5 mb-3 truncate">
              {product.serial}
            </p>

            {/* Warranty info */}
            <div className="flex items-center gap-1.5">
              {product.status === 'active' || product.status === 'expiring' ? (
                <>
                  <ShieldCheck size={12} className={product.status === 'expiring' ? 'text-warning' : 'text-success'} />
                  <span className={`text-xs font-500 ${product.status === 'expiring' ? 'text-warning' : 'text-success'}`}>
                    {product.status === 'expiring' ? `${product.daysLeft}d left` : `${product.daysLeft}d remaining`}
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle size={12} className="text-danger" />
                  <span className="text-xs font-500 text-danger">Warranty expired</span>
                </>
              )}
            </div>

            {/* Purchase date */}
            <p className="text-[10px] text-muted-foreground mt-1">Purchased {product.purchaseDate}</p>
          </div>
        ))}
      </div>
    </div>
  );
}